import { useState, useEffect } from "react";
import { doc, getDoc } from "firebase/firestore";
import { auth, firestore } from "../firebaseConfig";

/**
 * Hook that resolves whether the currently authenticated user holds the
 * CompanyAdmin role for the given company.
 *
 * Resolution order (mirrors Kotlin RoleResolver):
 *  1. If the user's Firestore document has accountType === "Company" they are
 *     the company owner and therefore a CompanyAdmin.
 *  2. Otherwise, check the role field on the employee sub-document at
 *     companies/{companyName}/employees/{uid}.
 *
 * Returns false while loading or when companyName is not yet known.
 *
 * @param {string} companyName
 * @returns {boolean}
 */
export function useIsCompanyAdmin(companyName) {
  const [isCompanyAdmin, setIsCompanyAdmin] = useState(false);

  useEffect(() => {
    if (!companyName) {
      setIsCompanyAdmin(false);
      return;
    }

    let active = true;

    async function checkRole(user) {
      if (!user) {
        if (active) setIsCompanyAdmin(false);
        return;
      }

      try {
        // 1. Check user-level accountType
        const userDocSnap = await getDoc(doc(firestore, "users", user.uid));
        if (!active) return;

        if (userDocSnap.exists()) {
          const userData = userDocSnap.data();
          if (userData.accountType === "Company") {
            setIsCompanyAdmin(true);
            return;
          }
        }

        // 2. Check employee-level role override
        const employeeDocSnap = await getDoc(
          doc(firestore, "companies", companyName, "employees", user.uid)
        );
        if (!active) return;

        setIsCompanyAdmin(
          employeeDocSnap.exists() &&
            employeeDocSnap.data().role === "CompanyAdmin"
        );
      } catch (err) {
        console.error("Error checking company admin role:", err);
        if (active) setIsCompanyAdmin(false);
      }
    }

    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        checkRole(user);
      } else {
        if (active) setIsCompanyAdmin(false);
      }
    });

    return () => {
      active = false;
      unsubscribe();
    };
  }, [companyName]);

  return isCompanyAdmin;
}
